import React, { useEffect, useState, Fragment } from "react";
import { Button, Modal } from "flowbite-react";
import clsx from "clsx";
import moment from "moment";
import axios, { apiBaseUrl } from "../services/http";
import { Dialog, Transition } from "@headlessui/react";
import { useAccountStore } from "../store";
import AsyncCreatableSelect from "react-select/async-creatable";
import debounce from "debounce-promise";

export default function BookingForm({ room, onUpdateTimeslot }) {
  const { fetch: fetchUser } = useAccountStore();
  const [active, setActive] = useState(false);
  const [addUsers, setAddUsers] = useState(
    Array(room.room_settings.min_users - 1).fill({ label: "", value: "" })
  );
  const [submit, setSubmit] = useState({
    status: "idle",
    data: null,
    error: null,
  });

  useEffect(() => {
    if (room) {
      setActive(true);
      setAddUsers(
        Array(room.room_settings.min_users - 1).fill({ label: "", value: "" })
      );
    }
  }, [room]);

  useEffect(() => {
    setSubmit((state) => ({
      status: "idle",
      data: null,
      error: null,
    }));
  }, [room]);

  const getSearchContactsResults = debounce(async (query) => {
    // if (!query) {
    //   return Promise.resolve({ options: [] });
    // }
    const response = await axios.get(`${apiBaseUrl}/api/mycontacts?q=${query}`);

    const options = response.data.map((user) => ({
      value: user.username,
      label: `${user.display_name} (${user.username})`,
    }));

    return options;
  }, 200);

  const onSubmit = async () => {
    setSubmit((state) => ({ ...state, status: "pending", error: null }));
    try {
      const response = await axios.post(`${apiBaseUrl}/api/rooms/${room.id}`, {
        ts_start: room.status.ts_start.replace(/[-\s:]/g, ""),
        ts_end: room.status.ts_end.replace(/[-\s:]/g, ""),
        add_users: addUsers.map(u => u.value),
      });
      fetchUser();
      setSubmit((state) => ({
        ...state,
        status: "succeeded",
        data: response.data,
      }));
    } catch (error) {
      setSubmit((state) => ({
        ...state,
        status: "failed",
        error: error.response.data,
      }));
    }
  };

  const onClose = () => {
    if (submit.status === "pending") return;

    if (submit.status === "succeeded") {
      onUpdateTimeslot({
        value: [submit.data.status.ts_start, submit.data.status.ts_end],
      });
    }

    setActive(false);
  };

  const onAddUserCreate = () => {
    if (room.room_settings.max_users - 1 > addUsers.length) {
      setAddUsers([...addUsers, { label: "", value: "" }]);
    }
  };

  const onAddUserRemove = (index) => {
    setAddUsers(u => u.filter((_, i) => i !== index));
  };

  return (
    <Transition appear show={active} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </div>
                <div>
                  {submit.status !== "succeeded" && (
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-bold leading-6 text-gray-900 mb-4"
                    >
                      {room.space.name} - {room.name}
                    </Dialog.Title>
                  )}
                  {submit.status === "succeeded" ? (
                    <div className="space-y-4 text-base">
                      <div className="flex flex-col space-y-4 items-center justify-center">
                        <svg
                          className="w-16 h-16 text-green-400"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                        <h3 className="text-lg font-bold">Booking Confirmed</h3>
                        <p className="text-center text-gray-500">
                          Your booking has been successfully placed and
                          confirmed. Please check in within{" "}
                          {submit.data.room_settings.checkin_mins} minutes after
                          your session begins.
                        </p>

                        <button
                          className="border rounded-lg hover:bg-gray-100 px-5 py-3 font-semibold text-gray-700"
                          onClick={onClose}
                        >
                          Close
                        </button>
                      </div>
                      <div></div>
                    </div>
                  ) : (
                    <div className="space-y-4 mb-4">
                      {submit.status === "failed" && (
                        <div
                          className="flex p-4 text-red-700 bg-red-100 rounded-lg"
                          role="alert"
                        >
                          <svg
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            className="flex-shrink-0 inline w-6 h-6 mr-3"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                            ></path>
                          </svg>
                          <div>{submit.error.message}</div>
                        </div>
                      )}

                      <div class="flex">
                        <div class="lg:w-40 w-1/4">
                          <label class="inline-block font-bold text-base">
                            Date:
                          </label>
                        </div>
                        <div class="flex-1 ml-4">
                          {moment(room.status.ts_start).format(
                            "YYYY-MM-DD (ddd)",
                          )}
                        </div>
                      </div>
                      <div class="flex">
                        <div class="lg:w-40 w-1/4">
                          <label class="inline-block font-bold text-base">
                            Time:
                          </label>
                        </div>
                        <div class="flex-1 ml-4">
                          {moment(room.status.ts_start).format("HH:mm")} -{" "}
                          {moment(room.status.ts_end).format("HH:mm")}
                        </div>
                      </div>
                      <div class="flex">
                        <div class="lg:w-40 w-1/4">
                          <label class="inline-block font-bold text-base">
                            User:
                          </label>
                        </div>
                        <div class="flex-1 ml-4">{room.user.display_name}</div>
                      </div>
                      <div class="flex">
                        <div class="lg:w-40 w-1/4">
                          <label class="inline-block font-bold text-base">
                            Facility:
                          </label>
                        </div>
                        <div class="flex-1 ml-4">{room.space.name}</div>
                      </div>
                      <div class="flex">
                        <div class="lg:w-40 w-1/4">
                          <label class="inline-block font-bold text-base">
                            Seat/Room No:
                          </label>
                        </div>
                        <div class="flex-1 ml-4">{room.name}</div>
                      </div>
                      <div class="flex">
                        <div class="lg:w-40 w-1/4">
                          <label class="inline-block font-bold text-base">
                            Session Length:
                          </label>
                        </div>
                        <div class="flex-1 ml-4">
                          {room.session_length.desc}
                        </div>
                      </div>
                      {room.room_settings.min_users > 1 && (
                        <div class="flex">
                          <div class="lg:w-40 w-1/4">
                            <label class="inline-block font-bold text-base">
                              Seating Capacity:
                            </label>
                          </div>
                          <div class="flex-1 ml-4">
                            <p>
                              {room.room_settings.min_users} to{" "}
                              {room.room_settings.max_users} users
                            </p>
                          </div>
                        </div>
                      )}
                      {room.room_settings.min_users > 1 && (
                        <div class="flex">
                          <div class="w-40">
                            <label class="inline-block font-bold text-base">
                              Additional Users:
                            </label>
                          </div>
                          <div class="flex-1 ml-4 space-y-2">
                            {addUsers.map((v, i) => (
                              <div
                                key={i}
                                className="relative flex items-center space-x-2"
                              >
                                {/* <input type="text" value={v} onChange={(e) => {
                                    setAddUsers(
                                      addUsers.map((value, j) =>
                                        i === j
                                          ? e.target.value
                                          : value
                                      )
                                    );
                                  }}/> */}
                                <AsyncCreatableSelect
                                  styles={{
                                    input: (base) => ({
                                      ...base,
                                      "input:focus": {
                                        boxShadow: "none",
                                      },
                                    }),
                                  }}
                                  components={{
                                    DropdownIndicator: () => null,
                                    IndicatorSeparator: () => null,
                                  }}
                                  placeholder={`${
                                    i < room.room_settings.min_users - 1
                                      ? "*"
                                      : ""
                                  } User ${i + 1} (e.g. tmchan / s1234567)`}
                                  defaultOptions
                                  value={
                                    addUsers[i]?.value ? addUsers[i] : null
                                  }
                                  loadOptions={getSearchContactsResults}
                                  formatCreateLabel={(input) =>
                                    `Add "${input}"`
                                  }
                                  isClearable={true}
                                  onChange={(option) => {
                                    setAddUsers((u) =>
                                      u.map((value, j) =>
                                        i === j
                                          ? option || { label: "", value: "" }
                                          : value,
                                      ),
                                    );
                                  }}
                                  className="w-80"
                                />
                                {i >= room.room_settings.min_users - 1 && (
                                  <button
                                    onClick={() => onAddUserRemove(i)}
                                    className="text-red-600 hover:underline flex items-center text-sm"
                                  >
                                    <svg
                                      className="w-4 h-4 inline-block mr-1"
                                      data-slot="icon"
                                      fill="none"
                                      stroke-width="2"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                      aria-hidden="true"
                                    >
                                      <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                      ></path>
                                    </svg>
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                            {addUsers.length <
                              room.room_settings.max_users - 1 && (
                              <button
                                onClick={onAddUserCreate}
                                className="text-orange-600 hover:underline flex items-center"
                              >
                                <svg
                                  className="w-4 h-4 inline-block mr-1"
                                  data-slot="icon"
                                  fill="none"
                                  stroke-width="2"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                  ></path>
                                </svg>
                                Add user
                              </button>
                            )}
                            {/* <div>
                              <p className="text-sm">
                                This room requires additional users who will be
                                checking in with you.
                              </p>

                              <a
                                href={room.space.info_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-orange-600 hover:underline text-sm"
                              >
                                Learn more
                                <svg
                                  className="w-4 h-4 inline-block ml-1"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="1.5"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                  ></path>
                                </svg>
                              </a>
                            </div> */}
                          </div>
                        </div>
                      )}
                      {room.room_settings.alert_message && (
                        <div
                          className="flex p-4 text-blue-800 bg-blue-50 rounded-lg"
                          role="alert"
                        >
                          <div>
                            <span className="font-bold">Alert:</span>{" "}
                            {room.room_settings.alert_message}
                          </div>
                        </div>
                      )}
                      <p className="text-base leading-relaxed text-gray-500">
                        Please check in within {room.room_settings.checkin_mins}{" "}
                        minutes after your session begins. Failure to do so will
                        have your booking cancelled and the seat will be
                        released to other users.
                      </p>
                    </div>
                  )}
                  {submit.status !== "succeeded" && (
                    <div className="flex space-x-4">
                      <button
                        className={clsx(
                          "bg-orange-500 hover:bg-orange-600 rounded-lg px-5 py-3 text-white font-semibold",
                          submit.status === "pending"
                            ? "opacity-50 pointer-events-none"
                            : "",
                        )}
                        disabled={submit.status === "pending"}
                        onClick={onSubmit}
                      >
                        {submit.status === "pending" && (
                          <svg
                            aria-hidden="true"
                            class="inline w-4 h-4 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                            viewBox="0 0 100 101"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                              fill="currentColor"
                            />
                            <path
                              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                              fill="currentFill"
                            />
                          </svg>
                        )}
                        Submit
                      </button>
                      <button
                        className={clsx(
                          "border rounded-lg hover:bg-gray-100 px-5 py-3 font-semibold text-gray-700",
                          submit.status === "pending"
                            ? "opacity-50 pointer-events-none"
                            : "",
                        )}
                        disabled={submit.status === "pending"}
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
